import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { Warehouse, Supplier, StockItem } from '../types';
import ProductDetailsDialog from './ProductDetailsDialog';
import '../core.css';
import LoadingSpinner from './LoadingSpinner';

type SortKey = 'product_name' | 'sku' | 'supplier_name' | 'fdc_stock' | 'target_fdc_stock' | 'stock_days' | 'stock_health' | 'recommended_ship_quantity';
type SortOrder = 'asc' | 'desc';

interface SortConfig {
  key: SortKey;
  order: SortOrder;
}

interface ThreePLHubProps {
  warehouseId: number;
}

interface RestockSuggestion {
  product_name: string;
  sku: string;
  supplier_name: string;
  fdc_stock: number;
  target_fdc_stock: number;
  stock_days: string;
  stock_health: string;
  recommended_ship_quantity: number;
  product_id: number;
  variation_id: number;
}

// Warehouse address used for PO ship-to and shipping-label ship-from
const ADDRESS_FIELDS = [
  { key: 'ship_from_name', label: 'Warehouse Name (first line of address)' },
  { key: 'ship_from_company', label: 'Company' },
  { key: 'ship_from_address1', label: 'Address Line 1' },
  { key: 'ship_from_address2', label: 'Address Line 2' },
  { key: 'ship_from_city', label: 'City' },
  { key: 'ship_from_state', label: 'Province / State' },
  { key: 'ship_from_zipcode', label: 'Postal / Zip Code' },
  { key: 'ship_from_country', label: 'Country (2-letter code)' },
  { key: 'ship_from_phone', label: 'Phone' },
  { key: 'ship_from_email', label: 'Email' },
] as const;

type AddressForm = Record<(typeof ADDRESS_FIELDS)[number]['key'], string>;

const EMPTY_ADDRESS = Object.fromEntries(ADDRESS_FIELDS.map((f) => [f.key, ''])) as AddressForm;

const ThreePLHub: React.FC<ThreePLHubProps> = ({ warehouseId }) => {
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<RestockSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'stock_days', order: 'asc' });
  const [newFdcMethod, setNewFdcMethod] = useState('');
  const [newFdcMethodCode, setNewFdcMethodCode] = useState('');
  const [fdcMethods, setFdcMethods] = useState<{ id: number; name: string; method_code: string }[]>([]);
  const [fdcMethodError, setFdcMethodError] = useState<string | null>(null);
  const [targetStockDays, setTargetStockDays] = useState(30);
  const [typicalShipmentPeriod, setTypicalShipmentPeriod] = useState(7);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<StockItem | null>(null);
  const [productDetailsOpen, setProductDetailsOpen] = useState(false);
  const [cronJobLoading, setCronJobLoading] = useState(false);
  const [cronJobMessage, setCronJobMessage] = useState<string | null>(null);
  const [processedOrders, setProcessedOrders] = useState<string[]>([]);
  const [address, setAddress] = useState<AddressForm>(EMPTY_ADDRESS);
  const [asnCompany, setAsnCompany] = useState('');
  const [asnFacility, setAsnFacility] = useState('');
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressMessage, setAddressMessage] = useState<{ text: string; error: boolean } | null>(null);
  const isFdc = warehouse?.name === 'FDC';

  const filteredSuggestions = suggestions
    .filter((item) => {
      if (supplierFilter === 'all') return true;
      if (supplierFilter === 'None') return item.supplier_name === 'None';
      return item.supplier_name === supplierFilter;
    })
    .sort((a, b) => {
      let aValue: any;
      let bValue: any;

      const { key, order } = sortConfig;

      switch (key) {
        case 'product_name':
          aValue = a.product_name.toLowerCase();
          bValue = b.product_name.toLowerCase();
          break;
        case 'sku':
          aValue = a.sku.toLowerCase();
          bValue = b.sku.toLowerCase();
          break;
        case 'supplier_name':
          aValue = a.supplier_name.toLowerCase();
          bValue = b.supplier_name.toLowerCase();
          break;
        case 'fdc_stock':
          aValue = Number(a.fdc_stock);
          bValue = Number(b.fdc_stock);
          break;
        case 'target_fdc_stock':
          aValue = Number(a.target_fdc_stock);
          bValue = Number(b.target_fdc_stock);
          break;
        case 'stock_days':
          aValue = parseFloat(a.stock_days) || Infinity;
          bValue = parseFloat(b.stock_days) || Infinity;
          break;
        case 'stock_health':
          aValue = parseFloat(a.stock_health) || 0;
          bValue = parseFloat(b.stock_health) || 0;
          break;
        case 'recommended_ship_quantity':
          aValue = Number(a.recommended_ship_quantity);
          bValue = Number(b.recommended_ship_quantity);
          break;
        default:
          aValue = a[key as keyof RestockSuggestion];
          bValue = b[key as keyof RestockSuggestion];
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

  const handleSort = (key: SortKey) => {
    setSortConfig(prev => ({
      key,
      order: prev.key === key && prev.order === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleProductClick = async (productId: number, variationId: number) => {
    try {
      const response = await api.get(`/product-stock?product_id=${productId}&variation_id=${variationId}`);
      if (response.data) {
        setSelectedProduct(response.data);
        setProductDetailsOpen(true);
      }
    } catch (err: any) {
      console.error('Failed to fetch product details:', err);
    }
  };

  const fetchWarehouse = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/neon-stock');
      const foundWarehouse = response.data.warehouses.find((w: Warehouse) => w.id === warehouseId);
      if (foundWarehouse) {
        setWarehouse(foundWarehouse);
        setTargetStockDays(foundWarehouse.target_stock_days || 30);
        // For now, set lastUpdated to current time. Later this will come from the API
        setLastUpdated(new Date().toLocaleString());
      } else {
        setError('Warehouse not found');
      }
      setSuppliers(response.data.suppliers || []);
    } catch (err: any) {
      console.error('Fetch warehouse error:', err);
      setError('Failed to load warehouse data');
    } finally {
      setLoading(false);
    }
  }, [warehouseId]);

  const fetchFdcMethods = useCallback(async () => {
    try {
      const response = await api.get('/fdc/fdc-shipment-methods');
      setFdcMethods(response.data.methods || []);
      setFdcMethodError(null);
    } catch (err: any) {
      console.error('Fetch FDC methods error:', err);
      setFdcMethodError('Failed to load FDC methods');
    }
  }, []);

  const fetchSuggestions = useCallback(async () => {
    if (!warehouse) return;
    setSuggestionsLoading(true);
    try {
      const response = await api.get(`/3pl-restock-suggestions?warehouse_id=${warehouseId}`);
      setSuggestions(response.data.suggestions);
    } catch (err: any) {
      console.error('Fetch suggestions error:', err);
    } finally {
      setSuggestionsLoading(false);
    }
  }, [warehouse, warehouseId]);

  const handleAddFdcMethod = async () => {
    if (!newFdcMethod.trim() || !newFdcMethodCode.trim()) {
      setFdcMethodError('Method name and code cannot be empty');
      return;
    }

    try {
      const response = await api.post('/fdc/add-fdc-shipment-method', {
        name: newFdcMethod.trim(),
        method_code: newFdcMethodCode.trim()
      });
      setFdcMethods([...fdcMethods, response.data.method]);
      setNewFdcMethod('');
      setNewFdcMethodCode('');
      setFdcMethodError(null);
    } catch (error: any) {
      console.error('Add FDC method error:', error.message);
      setFdcMethodError(error.response?.data?.error || 'Failed to add FDC shipment method');
    }
  };

  const handleRemoveFdcMethod = async (id: number) => {
    const method = fdcMethods.find((m) => m.id === id);
    if (!method) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete the FDC shipment method "${method.name}"?`
    );
    if (!confirmDelete) return;

    try {
      await api.delete('/fdc/delete-fdc-shipment-method', { data: { id } });
      setFdcMethods(fdcMethods.filter((method) => method.id !== id));
      setFdcMethodError(null);
    } catch (error: any) {
      console.error('Delete FDC method error:', error.message);
      setFdcMethodError(error.response?.data?.error || 'Failed to delete FDC shipment method');
    }
  };

  const handleSaveSettings = async () => {
    if (!warehouse) return;
    setSettingsLoading(true);
    try {
      await api.post('/update-warehouses', {
        warehouses: [{
          id: warehouse.id,
          name: warehouse.name,
          color: warehouse.color,
          barcode_shipping: warehouse.barcode_shipping,
          barcode_production: warehouse.barcode_production,
          "3PL_shipping": warehouse["3PL_shipping"],
          target_stock_days: targetStockDays
        }]
      });
      // Refresh suggestions with new target stock days
      await fetchSuggestions();
      setError(null);
    } catch (err: any) {
      console.error('Save settings error:', err);
      setError('Failed to save settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchAddressDetails = useCallback(async () => {
    try {
      const [detailsRes, asnRes] = await Promise.all([
        api.get(`/warehouse-details?warehouse_id=${warehouseId}`),
        api.get('/settings?key=fdc_asn'),
      ]);
      const details = detailsRes.data || {};
      setAddress(
        Object.fromEntries(ADDRESS_FIELDS.map((f) => [f.key, details[f.key] || ''])) as AddressForm
      );
      setAsnCompany(asnRes.data.fdc_asn_company || '');
      setAsnFacility(asnRes.data.fdc_asn_facility || '');
    } catch (err: any) {
      console.error('Fetch address details error:', err);
      setAddressMessage({ text: 'Failed to load address details', error: true });
    }
  }, [warehouseId]);

  const handleSaveAddress = async () => {
    if (isFdc && (!asnCompany.trim() || !asnFacility.trim())) {
      setAddressMessage({ text: 'ASN company and facility codes are required', error: true });
      return;
    }
    setAddressSaving(true);
    setAddressMessage(null);
    try {
      await api.put('/warehouse-details', { id: warehouseId, ...address });
      if (isFdc) {
        await api.post('/settings', {
          fdc_asn_company: asnCompany.trim(),
          fdc_asn_facility: asnFacility.trim(),
        });
      }
      await fetchAddressDetails();
      setAddressMessage({ text: 'Address details saved', error: false });
    } catch (err: any) {
      console.error('Save address error:', err);
      setAddressMessage({
        text: err.response?.data?.error || 'Failed to save address details',
        error: true,
      });
    } finally {
      setAddressSaving(false);
    }
  };

  const handleRunCronJob = async () => {
    setCronJobLoading(true);
    setCronJobMessage(null);
    setProcessedOrders([]);
    try {
      const response = await api.get('/fdc/cron-check-fdc-orders');
      setCronJobMessage(`Successfully updated ${response.data.processed} FDC orders.`);
      setProcessedOrders(response.data.orders || []);
    } catch (err: any) {
      console.error('Cron job error:', err);
      setCronJobMessage(`Failed to update FDC orders: ${err.response?.data?.error || err.message}`);
    } finally {
      setCronJobLoading(false);
    }
  };

  useEffect(() => {
    fetchWarehouse();
    fetchFdcMethods();
    fetchAddressDetails();
  }, [fetchWarehouse, fetchFdcMethods, fetchAddressDetails]);

  useEffect(() => {
    if (warehouse) {
      fetchSuggestions();
    }
  }, [warehouse, fetchSuggestions]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !warehouse) {
    return (
      <div className="container">
        <h2>Error</h2>
        <p>{error || 'Warehouse not found'}</p>
      </div>
    );
  }

  return (
    <div className="container">
      <h1>{warehouse.name} Hub</h1>
      <p className="last-updated">Last Updated: {lastUpdated}</p>
      <div className="admin-content-container">
        {/* Restock Suggestion Section */}
        <h2 className="section-heading">Restock Suggestion</h2>
        <p className="section-description">Products that may need restocking at {warehouse.name}.</p>
        <div className="search-container">
          <select
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
            className="supplier-filter"
          >
            <option value="all">All Suppliers</option>
            <option value="None">None</option>
            {suppliers.map((supplier) => (
              <option key={supplier.id} value={supplier.name}>
                {supplier.name}
              </option>
            ))}
          </select>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('product_name')} style={{ cursor: 'pointer' }}>
                Product Name {sortConfig.key === 'product_name' && (sortConfig.order === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('sku')} style={{ cursor: 'pointer' }}>
                SKU {sortConfig.key === 'sku' && (sortConfig.order === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('supplier_name')} style={{ cursor: 'pointer' }}>
                Supplier {sortConfig.key === 'supplier_name' && (sortConfig.order === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('fdc_stock')} style={{ cursor: 'pointer' }}>
                FDC Stock {sortConfig.key === 'fdc_stock' && (sortConfig.order === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('target_fdc_stock')} style={{ cursor: 'pointer' }}>
                Target FDC Stock {sortConfig.key === 'target_fdc_stock' && (sortConfig.order === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('stock_days')} style={{ cursor: 'pointer' }}>
                Stock Days {sortConfig.key === 'stock_days' && (sortConfig.order === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('stock_health')} style={{ cursor: 'pointer' }}>
                Stock Health {sortConfig.key === 'stock_health' && (sortConfig.order === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('recommended_ship_quantity')} style={{ cursor: 'pointer' }}>
                Recommended Ship Quantity {sortConfig.key === 'recommended_ship_quantity' && (sortConfig.order === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody>
            {suggestionsLoading ? (
              <tr>
                <td colSpan={8}>Loading restock suggestions...</td>
              </tr>
            ) : filteredSuggestions.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  {suggestions.length === 0 ? 'No products found with FDC item numbers' : 'No products match the selected supplier filter'}
                </td>
              </tr>
            ) : (
              filteredSuggestions.map((item, index) => (
                <tr key={index}>
                  <td>
                    <span
                      className="product-name"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleProductClick(item.product_id, item.variation_id)}
                    >
                      {item.product_name}
                    </span>
                  </td>
                  <td>{item.sku}</td>
                  <td>
                    <div className="supplier-tag-container">
                      <span
                        className="supplier-tag"
                        style={{
                          backgroundColor: suppliers.find(s => s.name === item.supplier_name)?.color || '#e0e0e0'
                        }}
                      >
                        {item.supplier_name}
                      </span>
                    </div>
                  </td>
                  <td>{item.fdc_stock}</td>
                  <td>{item.target_fdc_stock}</td>
                  <td>{item.stock_days}</td>
                  <td>
                    <span className={`stock-health-chip ${parseFloat(item.stock_health) < 20 ? 'stock-health-low' : parseFloat(item.stock_health) < 80 ? 'stock-health-medium' : 'stock-health-high'}`}>
                      {item.stock_health}%
                    </span>
                  </td>
                  <td>{item.recommended_ship_quantity}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <hr className="section-divider" />

        {/* Shipment Notifications Section */}
        <h2 className="section-heading">Shipment Notifications</h2>
        <p className="section-description">Manage ASN (shipment notifications) and shipments sent to {warehouse.name}.</p>
        <p>Coming soon...</p>

        <hr className="section-divider" />

        {/* Warehouse Stock Settings Section */}
        <h2 className="section-heading">{warehouse.name} Stock Settings</h2>
        <p className="section-description">Configure stock settings for {warehouse.name}.</p>
        <div className="settings-section">
          <div className="admin-add-section">
            <label>
              Target Stock (Days):
              <input
                type="number"
                value={targetStockDays}
                onChange={(e) => setTargetStockDays(Number(e.target.value))}
                className="admin-input"
              />
            </label>
          </div>
          <div className="admin-add-section">
            <label>
              Typical Shipment Period (Days):
              <input
                type="number"
                value={typicalShipmentPeriod}
                onChange={(e) => setTypicalShipmentPeriod(Number(e.target.value))}
                className="admin-input"
              />
            </label>
          </div>
          <button
            onClick={handleSaveSettings}
            disabled={settingsLoading}
            className="action-button"
          >
            {settingsLoading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

        <hr className="section-divider" />

        {/* Address & ASN Details Section */}
        <h2 className="section-heading">{warehouse.name} Address{isFdc ? ' & ASN Details' : ''}</h2>
        <p className="section-description">
          Used as the Ship To address on new purchase orders and supplier emails, and as the ship-from
          address on shipping labels from {warehouse.name}.
          {isFdc && ' The company and facility codes are written into the FDC ASN file.'}
        </p>
        <div className="settings-section">
          {isFdc && (
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px', marginBottom: '16px' }}
            >
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                ASN Company Code
                <input
                  type="text"
                  value={asnCompany}
                  onChange={(e) => setAsnCompany(e.target.value)}
                  className="admin-input"
                  maxLength={20}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                ASN Facility Code
                <input
                  type="text"
                  value={asnFacility}
                  onChange={(e) => setAsnFacility(e.target.value)}
                  className="admin-input"
                  maxLength={20}
                />
              </label>
            </div>
          )}
          <div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px', marginBottom: '16px' }}
          >
            {ADDRESS_FIELDS.map((field) => (
              <label key={field.key} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {field.label}
                <input
                  type="text"
                  value={address[field.key]}
                  onChange={(e) => setAddress((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  className="admin-input"
                  maxLength={255}
                />
              </label>
            ))}
          </div>
          <button onClick={handleSaveAddress} disabled={addressSaving} className="action-button">
            {addressSaving ? 'Saving...' : 'Save Address Details'}
          </button>
          {addressMessage && (
            <p className={addressMessage.error ? 'error-text' : 'success-text'}>{addressMessage.text}</p>
          )}
        </div>

        <hr className="section-divider" />

        {/* FDC Shipping Methods Section */}
        <h2 className="section-heading">FDC Shipping Methods</h2>
        <p className="section-description">Manage FDC shipping methods for 3PL orders.</p>
        
        {/* FDC Cron Job Section */}
        <div className="admin-add-section">
          <button 
            onClick={handleRunCronJob} 
            disabled={cronJobLoading}
            className="action-button"
          >
            {cronJobLoading ? 'Updating FDC Orders...' : 'Update FDC Orders'}
          </button>
          {cronJobMessage && (
            <p className={`cron-job-message ${cronJobMessage.includes('Failed') ? 'error-text' : 'success-text'}`}>
              {cronJobMessage}
            </p>
          )}
          {processedOrders.length > 0 && (
            <div className="processed-orders-list">
              <h4>Updated Orders:</h4>
              <ul>
                {processedOrders.map((order, index) => (
                  <li key={index}>{order}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {fdcMethodError && <p className="error-text">{fdcMethodError}</p>}
        <div className="admin-add-section">
          <input
            type="text"
            placeholder="Method name"
            value={newFdcMethod}
            onChange={(e) => setNewFdcMethod(e.target.value)}
            className="admin-input"
          />
          <input
            type="text"
            placeholder="Method code"
            value={newFdcMethodCode}
            onChange={(e) => setNewFdcMethodCode(e.target.value)}
            className="admin-input"
          />
          <button onClick={handleAddFdcMethod} className="action-button">
            Add
          </button>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Method Code</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {fdcMethods.length === 0 ? (
              <tr>
                <td colSpan={3}>No FDC methods configured</td>
              </tr>
            ) : (
              fdcMethods.map((method) => (
                <tr key={method.id}>
                  <td>{method.name}</td>
                  <td>{method.method_code}</td>
                  <td>
                    <button
                      className="action-button delete-button"
                      onClick={() => handleRemoveFdcMethod(method.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedProduct && productDetailsOpen && (
        <ProductDetailsDialog
          item={selectedProduct}
          onClose={() => {
            setProductDetailsOpen(false);
            setSelectedProduct(null);
          }}
          refreshStock={fetchSuggestions}
        />
      )}
    </div>
  );
};

export default ThreePLHub;
